import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { ZeosSpinner } from "../../../components/Shared/ZeosSpinner";
import { zeosDialog } from "../../../components/Shared/confirm-dialog/ConfirmDialog";
import { exploreSurveyConfigBySlug } from "../../../configs/ExploreSurveyConfig";
import {
  getOnboardingSurveyCard,
  getOnboardingSurveyCardBySlug,
  OnboardingSurveyCardId,
} from "../../../configs/OnboardingSurveyCardsConfig";
import { ZeosApiContext } from "../../../context/ZeosApiContext";
import { SurveyForm } from "../../../forms/SurveyForm";
import { useFormSubmission } from "../../../hooks/useFormSubmission";
import {
  clearSurveyDraft,
  clearSurveyHubSelection,
  dequeueSurvey,
  setSurveyDraft,
  surveyDraftSelector,
  surveyQueueSelector,
} from "../../../redux-store/OnboardingSlice";
import { RootState } from "../../../redux-store/Store";
import { IZeosUser } from "../../../services/auth/IZeosUser";
import { isDemo } from "../../../services/environment/EnvironmentHelpers";
import { createLogger } from "../../../services/Logger";
import { IOnboardStatus } from "../../../services/nav/Interfaces";
import { NavigationService } from "../../../services/nav/NavigationService";
import {
  buildSurveyUpdatePayload,
  hydrateSurveyAnswers,
} from "../../../services/survey/SurveyAnswerHelpers";
import { buildOnboardingAnswerSummary } from "../../../services/survey/OnboardingSurveySummaryHelpers";
import {
  flattenSurveyQuestions,
  isQuestionType,
  isQuestionVisible,
  pruneHiddenAnswers,
} from "../../../services/survey/SurveyVisibilityHelpers";
import { ISurveyContract } from "../../../services/zeos-api/contracts/Survey";
import {
  QuestionTypeEnum,
  SurveyCompletionMode,
} from "../../../services/zeos-api/enums/Survey";
import { REQUEST_ERROR } from "../../../services/zeos-api/ZeosApiExecutor";
import { OnboardContainer } from "../layout/OnboardContainer";
import {
  DeeperPathChoice,
  OnboardingSurveyCompletion,
} from "./OnboardingSurveyCompletion";
import { OnboardingComprehensiveSurvey } from "./OnboardingComprehensiveSurvey";

const log = createLogger("OnboardingSurvey");

type SurveyPhase = "form" | "completion" | "comprehensive";

export const OnboardingSurveyScreen: React.FC = () => {
  const { domainSlug } = useParams<{ domainSlug: string }>();
  const card = domainSlug
    ? getOnboardingSurveyCardBySlug(domainSlug)
    : undefined;
  const exploreConfig = exploreSurveyConfigBySlug(card?.id);

  const apiContext = useContext(ZeosApiContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const surveyQueue = useSelector(surveyQueueSelector);
  const draftAnswers = useSelector((state: RootState) =>
    card ? surveyDraftSelector(state, card.id) : null
  );

  const [answers, setAnswers] = useState<Record<number, any>>(draftAnswers ?? {});
  const [phase, setPhase] = useState<SurveyPhase>("form");
  const [deeperPath, setDeeperPath] = useState<DeeperPathChoice>("sufficient");
  const [isBackPage, setIsBackPage] = useState<boolean>(false);
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [originalAnswers, setOriginalAnswers] = useState<Record<number, any> | null>(
    null
  );
  const [template, setTemplate] = useState<ISurveyContract | null>(null);
  const [zeosUser, setZeosUser] = useState<IZeosUser>();

  useEffect(() => {
    if (!card) {
      navigate(NavigationService.buildOnboardingUrl("surveys"), {
        replace: true,
      });
    }
  }, [card, navigate]);

  useEffect(() => {
    setAnswers(draftAnswers ?? {});
    setOriginalAnswers(null);
    setTemplate(null);
    setPhase("form");
    setDeeperPath("sufficient");
  }, [card?.id]);

  const handleUserCallback = (user: IZeosUser): void => {
    setZeosUser(user);
  };

  const handleStatusCallback = (status: IOnboardStatus): void => {
    setIsBackPage(status.isBack);
  };

  const checkFormDirty = useCallback(() => {
    if (!originalAnswers) {
      return false;
    }
    return JSON.stringify(answers) !== JSON.stringify(originalAnswers);
  }, [answers, originalAnswers]);

  useEffect(() => {
    if (originalAnswers) {
      setIsFormDirty(checkFormDirty());
    }
  }, [answers, checkFormDirty, originalAnswers]);

  useEffect(() => {
    if (!card || isLoadingData || phase !== "form") {
      return;
    }
    dispatch(setSurveyDraft({ cardId: card.id, answers }));
  }, [answers, card, dispatch, isLoadingData, phase]);

  useEffect(() => {
    if (!card) {
      return;
    }

    let cancelled = false;

    const initAsync = async (): Promise<void> => {
      setLoading(true);
      try {
        const response = await apiContext.Survey.getSurveyTemplate(
          card.templateId,
          { mode: "onboarding" }
        );

        if (cancelled) {
          return;
        }

        if (response.data) {
          setTemplate(response.data);
        } else {
          log.error("Error loading survey:", response.message);
        }
      } catch (error) {
        if (!cancelled) {
          log.error("Onboarding survey init failed:", error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    initAsync();

    return () => {
      cancelled = true;
    };
  }, [apiContext.Survey, card]);

  useEffect(() => {
    const initAsync = async () => {
      if (zeosUser && template) {
        setIsLoadingData(true);
        try {
          await loadExistingSurveyData(zeosUser, template);
        } catch (error) {
          log.error("Exception while loading survey data", {
            error: error instanceof Error ? error.message : error,
            zeosUserId: zeosUser.zeosUserId,
          });
        } finally {
          setIsLoadingData(false);
        }
      }
    };
    void initAsync();
  }, [zeosUser?.zeosUserId, template]);

  const loadExistingSurveyData = async (
    user: IZeosUser,
    surveyTemplate: ISurveyContract
  ): Promise<void> => {
    if (!user?.zeosUserId) {
      return;
    }

    try {
      const resp = await apiContext.Survey.getUserSurvey(
        surveyTemplate.surveyTemplateId,
        user.zeosUserId
      );

      const latest = resp?.data;
      if (latest && Array.isArray(latest.answers) && latest.answers.length > 0) {
        const next = hydrateSurveyAnswers(surveyTemplate, latest.answers);
        setAnswers(next);
        setOriginalAnswers(next);
        setIsFormDirty(false);
      }
    } catch (error) {
      log.error("Onboarding survey hydrate failed:", error);
    }
  };

  const submitSurveyData = async (
    formData: Record<number, any>,
    _mode: "put" | "post"
  ): Promise<void> => {
    if (!zeosUser?.zeosUserId || !template || !card) {
      throw new Error("No user data or survey template available");
    }

    const visibleOnly = pruneHiddenAnswers(template, formData);
    const instance = buildSurveyUpdatePayload(
      template,
      zeosUser.zeosUserId,
      visibleOnly,
      { completionMode: SurveyCompletionMode.OnboardingShort }
    );

    const putResult = await apiContext.Survey.updateSurveyInstance(instance);
    const putFailed =
      putResult?.zeosStatus?.toLowerCase() === "error" ||
      putResult?.message === REQUEST_ERROR ||
      putResult?.data === false;

    if (putFailed) {
      const postResult = await apiContext.Survey.submitSurveyInstance(instance);
      const postFailed =
        postResult?.zeosStatus?.toLowerCase() === "error" ||
        postResult?.message === REQUEST_ERROR;

      if (postFailed) {
        throw new Error("Survey submission failed");
      }
    }

    dispatch(clearSurveyDraft(card.id));
  };

  const { handleSubmission } = useFormSubmission<Record<number, any>>(
    isBackPage,
    submitSurveyData
  );

  const validateVisibleAnswers = (surveyTemplate: ISurveyContract): boolean => {
    for (const question of flattenSurveyQuestions(surveyTemplate)) {
      if (!isQuestionVisible(question, answers)) {
        continue;
      }
      if (isQuestionType(question.questionTypeId, QuestionTypeEnum.FreeText)) {
        continue;
      }

      const value = answers[question.questionId];
      const minSelect = question.minSelect ?? 1;

      if (
        isQuestionType(question.questionTypeId, QuestionTypeEnum.MultiChoice) ||
        isQuestionType(question.questionTypeId, QuestionTypeEnum.RankedChoice)
      ) {
        const selected = Array.isArray(value) ? value : [];
        if (selected.length < minSelect) {
          zeosDialog(
            minSelect === 1
              ? "Please select an answer."
              : `Please select at least ${minSelect} options.`
          );
          return false;
        }
        continue;
      }

      if (value === undefined || value === null || value === "") {
        zeosDialog("Please select an answer.");
        return false;
      }
    }

    return true;
  };

  const summaryRows = useMemo(() => {
    if (!card || !template) {
      return [];
    }
    return buildOnboardingAnswerSummary(
      template,
      answers,
      card.summaryLabels
    );
  }, [answers, card, template]);

  const navigateAfterSurvey = useCallback(
    (completedCardId: OnboardingSurveyCardId) => {
      dispatch(dequeueSurvey(completedCardId));

      const remaining = surveyQueue.filter((id) => id !== completedCardId);
      if (remaining.length > 0) {
        const nextId = remaining[0];
        const nextCard = getOnboardingSurveyCard(nextId);
        if (nextCard?.enabled && nextCard.route) {
          if (nextCard.kind === "explore") {
            dispatch(dequeueSurvey(nextId));
            navigate(nextCard.route);
            return;
          }
          navigate(nextCard.route);
          return;
        }
        navigate(NavigationService.buildOnboardingUrl("surveys"));
        return;
      }

      dispatch(clearSurveyHubSelection());
      if (isDemo()) {
        navigate(NavigationService.buildOnboardingUrl("summary"));
        return;
      }
      navigate(NavigationService.buildOnboardingUrl("uapi"));
    },
    [dispatch, navigate, surveyQueue]
  );

  const handleNext = async (doNavigate: () => void): Promise<void> => {
    if (!card) {
      return;
    }

    if (phase === "completion") {
      void doNavigate;
      if (deeperPath === "comprehensive") {
        setPhase("comprehensive");
        return;
      }
      navigateAfterSurvey(card.id);
      return;
    }

    if (!template) {
      log.info("Survey template not found, skipping validation and submission");
      navigateAfterSurvey(card.id);
      return;
    }

    if (isLoadingData && !isFormDirty) {
      setPhase("completion");
      return;
    }

    if (!validateVisibleAnswers(template)) {
      return;
    }

    try {
      await handleSubmission(answers, async () => {
        void doNavigate;
        setPhase("completion");
        setDeeperPath("sufficient");
      });
    } catch (error) {
      console.error("Failed to complete onboarding survey step:", error);
    }
  };

  if (!card) {
    return null;
  }

  let content = <></>;

  if (loading || isLoadingData) {
    content = <ZeosSpinner />;
  } else if (!template) {
    content = <p>Survey not found.</p>;
  } else if (phase === "completion") {
    content = (
      <div className="space-y-6">
        <OnboardingSurveyCompletion
          heading={card.completionHeading}
          body={card.completionBody}
          rows={summaryRows}
          deeperPathChoice={deeperPath}
          onDeeperPathChange={setDeeperPath}
          domainTitle={card.pageTitle}
        />
      </div>
    );
  } else if (phase === "comprehensive") {
    content = (
      <OnboardingComprehensiveSurvey
        templateId={card.templateId}
        domainTitle={card.pageTitle}
        summaryTitle={exploreConfig?.summaryTitle ?? `Your ${card.pageTitle} profile is ready`}
        summaryBody={
          exploreConfig?.summaryBody ??
          `Thanks for sharing your priorities. We'll use this to tailor the ${card.pageTitle} pathway for this property.`
        }
        zeosUser={zeosUser}
        initialAnswers={answers}
        onBackToCompletion={() => setPhase("completion")}
        onFinished={() => navigateAfterSurvey(card.id)}
      />
    );
  } else {
    content = (
      <div className="space-y-6">
        <h2 className="text-2xl font-semibold text-zeos-black">
          {card.pageTitle}
        </h2>
        {card.pageMessage ? (
          <p className="text-sm text-gray-600 -mt-4">{card.pageMessage}</p>
        ) : null}
        <SurveyForm
          template={template}
          answers={answers}
          setAnswers={setAnswers}
          onSubmit={() => {}}
        />
      </div>
    );
  }

  return (
    <OnboardContainer
      content={content}
      contentType="display"
      pageSlug="survey"
      pageTitle=""
      pageMessage=""
      continueLabel={
        phase === "completion"
          ? deeperPath === "comprehensive"
            ? "Start Comprehensive Survey"
            : "Continue Onboarding"
          : undefined
      }
      hideBackButton={phase === "comprehensive"}
      hideContentCard={phase === "comprehensive"}
      hideContinueButton={phase === "comprehensive"}
      onNext={handleNext}
      userCallback={handleUserCallback}
      statusCallback={handleStatusCallback}
    />
  );
};
